import { RatioData, LotRatio } from './projectsData';
import { TypologieProgramme, ModaliteProgramme, Gamme } from './projectParameters';

export interface LotDistribution {
  lotName: string;
  categorie: string;
  nbProjets: number;
  min: number;
  p25: number;
  median: number;
  p75: number;
  p90: number;
  max: number;
  mean: number;
  stdDev: number;
  cv: number;         // coefficient of variation (0-1), mesure la variabilité relative
  weightPct: number;  // % moyen du coût total projet
}

export interface SimilarityParams {
  surface: number;
  typologie: TypologieProgramme | '';
  modalite: ModaliteProgramme | '';
  gamme: Gamme | '';
}

export function computePercentile(sortedValues: number[], p: number): number {
  if (sortedValues.length === 0) return 0;
  if (sortedValues.length === 1) return sortedValues[0];
  const index = (p / 100) * (sortedValues.length - 1);
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  const fraction = index - lower;
  return sortedValues[lower] + fraction * (sortedValues[upper] - sortedValues[lower]);
}

export function computeLotStats(projects: RatioData[], lotName: string): LotDistribution | null {
  const lotEntries = projects.flatMap(p =>
    p.lots
      .filter(l => l.nom === lotName)
      .map(l => ({ ratioM2: l.ratioM2, pourcentageProjet: l.pourcentageProjet, categorie: l.categorie }))
  );

  if (lotEntries.length === 0) return null;

  const values = lotEntries.map(e => e.ratioM2).sort((a, b) => a - b);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
  const stdDev = Math.sqrt(variance);
  const weightPct = lotEntries.reduce((sum, e) => sum + e.pourcentageProjet, 0) / lotEntries.length;

  return {
    lotName,
    categorie: lotEntries[0].categorie,
    nbProjets: values.length,
    min: values[0],
    p25: Math.round(computePercentile(values, 25)),
    median: Math.round(computePercentile(values, 50)),
    p75: Math.round(computePercentile(values, 75)),
    p90: Math.round(computePercentile(values, 90)),
    max: values[values.length - 1],
    mean: Math.round(mean),
    stdDev: Math.round(stdDev),
    cv: mean > 0 ? stdDev / mean : 0,
    weightPct: Math.round(weightPct * 100) / 100,
  };
}

export function computeAllLotsStats(projects: RatioData[]): Map<string, LotDistribution> {
  const lotNames = new Set(projects.flatMap(p => p.lots.map(l => l.nom)));
  const result = new Map<string, LotDistribution>();
  lotNames.forEach(name => {
    const stats = computeLotStats(projects, name);
    if (stats) result.set(name, stats);
  });
  return result;
}

export function computeRiskScore(dist: LotDistribution, allDists: LotDistribution[]): number {
  const maxCV = Math.max(...allDists.map(d => d.cv), 0.001);
  const maxWeight = Math.max(...allDists.map(d => d.weightPct), 0.001);
  const cvNorm = maxCV > 0 ? dist.cv / maxCV : 0;
  const weightNorm = maxWeight > 0 ? dist.weightPct / maxWeight : 0;
  return Math.round((0.5 * cvNorm + 0.5 * weightNorm) * 100);
}

export function scoreSimilarity(project: RatioData, params: SimilarityParams): number {
  let score = 0;
  let maxScore = 0;

  // Typologie : poids dominant — structure de coût entièrement différente selon type
  maxScore += 40;
  if (params.typologie && project.parameters.TypologieProgramme === params.typologie) score += 40;

  // Gamme : détermine le niveau de coût absolu
  maxScore += 30;
  if (params.gamme && project.parameters.Gamme === params.gamme) score += 30;

  // Modalité : neuf vs réhabilitation change la structure des lots
  maxScore += 20;
  if (params.modalite && project.parameters.ModaliteProgramme === params.modalite) score += 20;

  // Surface : effets d'échelle (grands projets ont des ratios fixes dilués)
  if (params.surface > 0) {
    maxScore += 10;
    const ratio = Math.min(params.surface, project.surface) / Math.max(params.surface, project.surface);
    score += Math.round(ratio * 10);
  }

  return maxScore > 0 ? Math.round((score / maxScore) * 100) : 0;
}

// ─── Navigation hiérarchique ─────────────────────────────────────────────────

/** Navigue dans l'arbre d'un lot en suivant un chemin de noms (sans le nom racine). */
function getNodeAtPath(lot: LotRatio, path: string[]): LotRatio | null {
  if (path.length === 0) return lot;
  const child = lot.enfants?.find(e => e.nom === path[0]);
  if (!child) return null;
  return getNodeAtPath(child, path.slice(1));
}

/**
 * Calcule les stats de distribution pour n'importe quel nœud de l'arbre.
 * path = ['CVC']                          → lot racine
 * path = ['CVC', 'Chauffage']             → ensemble
 * path = ['CVC', 'Chauffage', 'Émetteurs']→ sous-ensemble
 */
export function computeNodeStats(projects: RatioData[], path: string[]): LotDistribution | null {
  if (path.length === 0) return null;
  const [rootName, ...restPath] = path;

  const entries = projects.flatMap(p => {
    const rootLot = p.lots.find(l => l.nom === rootName);
    if (!rootLot) return [];
    const node = restPath.length === 0 ? rootLot : getNodeAtPath(rootLot, restPath);
    if (!node) return [];
    return [{ ratioM2: node.ratioM2, pourcentageProjet: node.pourcentageProjet, categorie: node.categorie }];
  });

  if (entries.length === 0) return null;

  const values = entries.map(e => e.ratioM2).sort((a, b) => a - b);
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, v) => sum + (v - mean) ** 2, 0) / values.length;
  const stdDev = Math.sqrt(variance);
  const weightPct = entries.reduce((sum, e) => sum + e.pourcentageProjet, 0) / entries.length;

  return {
    lotName: path[path.length - 1],
    categorie: entries[0].categorie,
    nbProjets: values.length,
    min: values[0],
    p25: Math.round(computePercentile(values, 25)),
    median: Math.round(computePercentile(values, 50)),
    p75: Math.round(computePercentile(values, 75)),
    p90: Math.round(computePercentile(values, 90)),
    max: values[values.length - 1],
    mean: Math.round(mean),
    stdDev: Math.round(stdDev),
    cv: mean > 0 ? stdDev / mean : 0,
    weightPct: Math.round(weightPct * 100) / 100,
  };
}

/** Retourne les stats de tous les enfants directs d'un nœud donné. */
export function computeAllChildrenStats(
  projects: RatioData[],
  parentPath: string[]
): Map<string, LotDistribution> {
  const [rootName, ...restPath] = parentPath;

  const childNames = new Set<string>();
  projects.forEach(p => {
    const rootLot = p.lots.find(l => l.nom === rootName);
    if (!rootLot) return;
    const parentNode = restPath.length === 0 ? rootLot : getNodeAtPath(rootLot, restPath);
    parentNode?.enfants?.forEach(e => childNames.add(e.nom));
  });

  const result = new Map<string, LotDistribution>();
  childNames.forEach(childName => {
    const stats = computeNodeStats(projects, [...parentPath, childName]);
    if (stats) result.set(childName, stats);
  });
  return result;
}

/** Vérifie si un nœud a des enfants dans au moins un des projets comparables. */
export function hasChildrenAtPath(projects: RatioData[], path: string[]): boolean {
  const [rootName, ...restPath] = path;
  return projects.some(p => {
    const rootLot = p.lots.find(l => l.nom === rootName);
    if (!rootLot) return false;
    const node = restPath.length === 0 ? rootLot : getNodeAtPath(rootLot, restPath);
    return (node?.enfants?.length ?? 0) > 0;
  });
}

export function findSimilarProjects(
  allProjects: RatioData[],
  params: SimilarityParams,
  topN = 3
): Array<{ project: RatioData; score: number }> {
  return allProjects
    .map(p => ({ project: p, score: scoreSimilarity(p, params) }))
    .filter(r => r.score > 30)
    .sort((a, b) => b.score - a.score)
    .slice(0, topN);
}
